import { useEffect, useState } from 'react';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import {
  updateVisit,
  VISIT_EDIT_PRICE_CAP_CENTS,
  type CutQuality,
  type PaymentMethod,
  type VisitRow,
} from '@busy/api';
import { useT } from '../lib/i18n';
import { PaymentMethodChips, ClientSourceButton, QualityPicker } from './cutTimer/PostCutSummary';

// Edit sheet for a saved visit. Surfaces ONLY the user-correctable
// fields enforced by migration 0059's visits_update_guard trigger:
//
//   - price_cents   (any direction, capped at $500)
//   - duration_min  (down-only — UI clamps + DB enforces)
//   - cut_type      (free text — style label)
//   - notes         (free text)
//
// Locked fields (started_at, had_before_after) are intentionally
// absent — there is no way for the barber to construct an update
// payload that touches them through this UI.
//
// The DB trigger is the source of truth; this form's client-side
// validation is purely for friendlier error surfacing before the
// round trip.

const PRICE_CAP_DOLLARS = VISIT_EDIT_PRICE_CAP_CENTS / 100;

export function EditVisitSheet({
  visit,
  onClose,
}: {
  visit: VisitRow;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();

  // Form state mirrors the visit's current values; the user mutates
  // these and on save we diff against `visit` to build a minimal patch.
  const [priceStr, setPriceStr] = useState<string>(
    (visit.price_cents / 100).toFixed(2),
  );
  const [durationStr, setDurationStr] = useState<string>(
    visit.duration_min == null ? '' : String(visit.duration_min),
  );
  const [cutType, setCutType] = useState<string>(visit.cut_type ?? '');
  const [notes, setNotes] = useState<string>(visit.notes ?? '');
  // Tip + payment method — captured at save (migrations 0065 + 0071)
  // but editable within the 24h window via this sheet so a barber can
  // backfill what they forgot to capture in the post-cut flow.
  const [tipStr, setTipStr] = useState<string>(
    visit.tip_cents > 0 ? (visit.tip_cents / 100).toFixed(2) : '',
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    visit.payment_method ?? null,
  );
  const [wasAppointment, setWasAppointment] = useState<boolean | null>(
    visit.was_appointment ?? null,
  );
  // Self-rated cut quality (migration 0075). Backfillable within the
  // 24h edit window. Other cut-timer signals (phase_times, paused_*,
  // phases_skipped) are NOT editable — they're machine-captured during
  // the cut itself and shouldn't be hand-corrected.
  const [quality, setQuality] = useState<CutQuality | null>(
    visit.quality ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync whenever a different visit is passed (parent reuses the
  // sheet component across rows by swapping the prop).
  useEffect(() => {
    setPriceStr((visit.price_cents / 100).toFixed(2));
    setDurationStr(visit.duration_min == null ? '' : String(visit.duration_min));
    setCutType(visit.cut_type ?? '');
    setNotes(visit.notes ?? '');
    setTipStr(visit.tip_cents > 0 ? (visit.tip_cents / 100).toFixed(2) : '');
    setPaymentMethod(visit.payment_method ?? null);
    setWasAppointment(visit.was_appointment ?? null);
    setQuality(visit.quality ?? null);
    setError(null);
  }, [visit.id]);

  // Original duration cap — UI prevents typing a higher value, but the
  // DB also rejects it. Null duration = no cap to clamp against.
  const maxDuration = visit.duration_min ?? null;

  async function handleSave() {
    setSaving(true);
    setError(null);

    // Parse price: accept "12", "12.50", "$12.50". Round to cents.
    const priceNumber = Number(priceStr.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      setError(t('cutlist.edit.errInvalidPrice'));
      setSaving(false);
      return;
    }
    const priceCentsNew = Math.round(priceNumber * 100);
    if (priceCentsNew > VISIT_EDIT_PRICE_CAP_CENTS) {
      setError(
        t('cutlist.edit.errPriceCap', { cap: String(PRICE_CAP_DOLLARS) }),
      );
      setSaving(false);
      return;
    }

    // Parse duration: integer minutes, must not exceed original.
    let durationNew: number | null = null;
    if (durationStr.trim() !== '') {
      const d = Number(durationStr);
      if (!Number.isInteger(d) || d < 0) {
        setError(t('cutlist.edit.errInvalidDuration'));
        setSaving(false);
        return;
      }
      durationNew = d;
      if (maxDuration != null && d > maxDuration) {
        setError(t('cutlist.edit.errDurationUp'));
        setSaving(false);
        return;
      }
    }

    // Parse tip — empty string clears to 0; range-checked against the
    // $500 CHECK in migration 0065.
    let tipCentsNew: number | null = null;
    if (tipStr.trim() === '') {
      tipCentsNew = 0;
    } else {
      const tipNumber = Number(tipStr.replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(tipNumber) || tipNumber < 0) {
        setError(t('cutlist.edit.errInvalidTip'));
        setSaving(false);
        return;
      }
      if (tipNumber > 500) {
        setError(t('cutlist.edit.errTipCap'));
        setSaving(false);
        return;
      }
      tipCentsNew = Math.round(tipNumber * 100);
    }

    // Build minimal patch — only fields that actually changed.
    const patch: {
      price_cents?: number;
      duration_min?: number;
      cut_type?: string | null;
      notes?: string | null;
      payment_method?: PaymentMethod | null;
      tip_cents?: number;
      was_appointment?: boolean | null;
      quality?: CutQuality | null;
    } = {};
    if (priceCentsNew !== visit.price_cents) patch.price_cents = priceCentsNew;
    if (durationNew !== null && durationNew !== visit.duration_min) {
      patch.duration_min = durationNew;
    }
    const normalizedCut = cutType.trim() === '' ? null : cutType.trim();
    if (normalizedCut !== (visit.cut_type ?? null)) patch.cut_type = normalizedCut;
    const normalizedNotes = notes.trim() === '' ? null : notes;
    if (normalizedNotes !== (visit.notes ?? null)) patch.notes = normalizedNotes;
    if (tipCentsNew !== visit.tip_cents) patch.tip_cents = tipCentsNew;
    if (paymentMethod !== (visit.payment_method ?? null)) {
      patch.payment_method = paymentMethod;
    }
    if (wasAppointment !== (visit.was_appointment ?? null)) {
      patch.was_appointment = wasAppointment;
    }
    if (quality !== (visit.quality ?? null)) {
      patch.quality = quality;
    }

    if (Object.keys(patch).length === 0) {
      toast(t('cutlist.edit.noChanges'), 'info');
      setSaving(false);
      onClose();
      return;
    }

    const res = await updateVisit(
      visit.id,
      { started_at: visit.started_at, duration_min: visit.duration_min },
      patch,
    );
    if (res.error) {
      setError(res.error.message);
      setSaving(false);
      return;
    }
    toast(t('cutlist.edit.saved'), 'success');
    setSaving(false);
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('cutlist.edit.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 md:p-6">
        <header className="mb-4 pr-10">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('cutlist.edit.eyebrow')}
          </div>
          <h2 className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight">
            {t('cutlist.edit.title')}
          </h2>
          <p className="text-[12px] text-ink-500 mt-1.5 leading-relaxed">
            {t('cutlist.edit.note')}
          </p>
        </header>

        <div className="space-y-4">
          {/* Price */}
          <label className="block">
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.priceLabel')}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-[15px]">
                $
              </span>
              <input
                inputMode="decimal"
                type="text"
                value={priceStr}
                onChange={(e) => setPriceStr(e.target.value)}
                className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-line bg-surface text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-gold-500/40"
              />
            </div>
            <p className="text-[11px] text-ink-500 mt-1">
              {t('cutlist.edit.priceHelp', { cap: String(PRICE_CAP_DOLLARS) })}
            </p>
          </label>

          {/* Duration */}
          <label className="block">
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.durationLabel')}
            </div>
            <div className="relative">
              <input
                inputMode="numeric"
                type="text"
                value={durationStr}
                onChange={(e) => setDurationStr(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full pl-3 pr-12 py-2.5 rounded-lg border border-line bg-surface text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-gold-500/40"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 text-[13px]">
                min
              </span>
            </div>
            <p className="text-[11px] text-ink-500 mt-1">
              {maxDuration != null
                ? t('cutlist.edit.durationHelp', { max: String(maxDuration) })
                : t('cutlist.edit.durationHelpNoMax')}
            </p>
          </label>

          {/* Style / cut_type */}
          <label className="block">
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.styleLabel')}
            </div>
            <input
              type="text"
              value={cutType}
              onChange={(e) => setCutType(e.target.value)}
              placeholder={t('cutlist.edit.stylePlaceholder')}
              className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-[15px] focus:outline-none focus:ring-2 focus:ring-gold-500/40"
            />
          </label>

          {/* Notes */}
          <label className="block">
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.notesLabel')}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('cutlist.edit.notesPlaceholder')}
              className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-[14px] focus:outline-none focus:ring-2 focus:ring-gold-500/40 resize-none"
            />
          </label>

          {/* Tip — editable within the 24h window so a barber can
              backfill what they forgot to capture in the post-cut flow. */}
          <label className="block">
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.tipLabel')}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-[15px]">
                $
              </span>
              <input
                inputMode="decimal"
                type="text"
                value={tipStr}
                onChange={(e) => setTipStr(e.target.value)}
                placeholder="0"
                className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-line bg-surface text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-gold-500/40"
              />
            </div>
            <p className="text-[11px] text-ink-500 mt-1">
              {t('cutlist.edit.tipHelp')}
            </p>
          </label>

          {/* Payment method — six-chip picker. Backfilling this on
              older cuts is the main use case here; lets the barber
              prep for tax season by classifying historical visits
              against each 1099-K source. */}
          <div>
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.paymentLabel')}
            </div>
            <PaymentMethodChips value={paymentMethod} onChange={setPaymentMethod} />
            <p className="text-[11px] text-ink-500 mt-1.5">
              {t('cutlist.edit.paymentHelp')}
            </p>
          </div>

          {/* Client source — single button (walk-in / appointment).
              Drives walk-in % on capacity + recaps (migration 0072). */}
          <div>
            <ClientSourceButton value={wasAppointment} onChange={setWasAppointment} />
            <p className="text-[11px] text-ink-500 mt-1.5">
              {t('cutlist.edit.sourceHelp')}
            </p>
          </div>

          {/* Cut quality — backfillable self-rating (migration 0075).
              Most useful for editing a recent cut where the barber
              skipped the picker at save and now wants to capture it. */}
          <div>
            <div className="text-[12px] font-semibold text-ink-700 mb-1.5">
              {t('cutlist.edit.qualityLabel')}
            </div>
            <QualityPicker value={quality} onChange={setQuality} />
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 px-3 py-2 text-[13px]">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[14px] font-semibold text-ink-700 hover:bg-bg-alt"
          >
            {t('cutlist.edit.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-[14px] font-bold bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? t('cutlist.edit.saving') : t('cutlist.edit.save')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
